import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartEmpty,
  ChartTooltip,
  GRID,
  Legend,
  SERIES,
  axisTick,
  compactTick,
} from "./chart-bits";

type Props = { data: any[] | null };

const KEYS = [
  { key: "totalDebt", label: "Total debt", color: SERIES[0] },
  { key: "totalCollected", label: "Collected", color: SERIES[1] },
];

/**
 * Debt raised vs collected, by month.
 *
 * Grouped rather than stacked: collected is a *part* of debt, so stacking them would
 * imply a total that doesn't exist. Two measures on one scale, one axis — a second
 * y-axis here would let the two series be scaled into any relationship you like.
 */
export default function ChasesChart({ data }: Props) {
  const rows = data ?? [];
  const hasValues = rows.some((r) => KEYS.some((k) => Number(r?.[k.key] ?? 0) > 0));

  if (!rows.length || !hasValues) return <ChartEmpty />;

  return (
    <div className="flex flex-col h-full">
      <Legend items={KEYS} className="mb-3" />

      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            margin={{ top: 4, right: 4, left: -18, bottom: 0 }}
            barGap={4}
          >
            <CartesianGrid stroke={GRID} strokeWidth={1} vertical={false} />
            <XAxis
              dataKey="month"
              tick={axisTick}
              tickLine={false}
              axisLine={{ stroke: GRID }}
            />
            <YAxis
              tick={axisTick}
              tickLine={false}
              axisLine={false}
              width={52}
              tickFormatter={compactTick}
            />
            <Tooltip
              cursor={{ fill: GRID }}
              content={({ active, payload, label }) =>
                active && payload?.length ? (
                  <ChartTooltip
                    title={label}
                    currency
                    rows={KEYS.map((k) => ({
                      label: k.label,
                      color: k.color,
                      value: payload.find((p) => p.dataKey === k.key)?.value,
                    }))}
                  />
                ) : null
              }
            />

            {KEYS.map((k) => (
              <Bar
                key={k.key}
                dataKey={k.key}
                fill={k.color}
                maxBarSize={24}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
